import { useEffect, useState } from "react";
import { Plus, Sparkles, Download, Calendar, Trash2, Save, Wand } from "lucide-react";
import { api, apiErrorMessage, API_BASE } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const TOPICS = ["safety", "ppe", "ladder", "lead_paint", "product", "customer", "quality", "other"];
const COHORTS = ["all", "crew", "contractor", "supervisor"];

export default function AdminTraining() {
    const [modules, setModules] = useState([]);
    const [assignments, setAssignments] = useState(null);
    const [compliance, setCompliance] = useState([]);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [pickModule, setPickModule] = useState("");
    const [pickCohort, setPickCohort] = useState("all");
    const [savedMsg, setSavedMsg] = useState("");

    const load = async () => {
        try {
            const [m, a, c] = await Promise.all([
                api.get("/admin/training/modules"),
                api.get("/admin/training/assignments"),
                api.get("/admin/training/compliance"),
            ]);
            setModules(m.data.modules);
            setAssignments(a.data);
            setCompliance(c.data.users);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => { load(); }, []);

    const assignWeek = async () => {
        if (!pickModule) return;
        setBusy(true);
        setSavedMsg("");
        try {
            const { data } = await api.post("/admin/training/assign-week", { module_id: pickModule, cohort: pickCohort });
            setSavedMsg(`Assigned ${data.created} user(s), skipped ${data.skipped}.`);
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally { setBusy(false); }
    };

    const removeModule = async (id) => {
        if (!window.confirm("Delete this module?")) return;
        try { await api.delete(`/admin/training/modules/${id}`); await load(); }
        catch (e) { setErr(apiErrorMessage(e)); }
    };

    const metrics = assignments?.metrics || { total: 0, completed: 0, completion_rate: 0, overdue: 0 };

    return (
        <div className="page-shell">
            <header className="fade-up flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="eyebrow">Training</p>
                    <h1 className="h1 mt-3" data-testid="at-title">Compliance & video library</h1>
                    <p className="text-muted mt-2 max-w-[640px]">Manage modules, pick this week's training, and pull OSHA-ready reports.</p>
                </div>
                <a
                    href={`${API_BASE}/admin/training/export.csv`}
                    target="_blank" rel="noreferrer"
                    className="btn btn-secondary"
                    data-testid="at-export"
                >
                    <Download size={14} />
                    Export CSV
                </a>
            </header>

            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                <Metric label="This week" value={metrics.total} testId="m-week" />
                <Metric label="Completed" value={metrics.completed} testId="m-done" />
                <Metric label="Completion rate" value={`${metrics.completion_rate}%`} testId="m-rate" />
                <Metric label="Overdue" value={metrics.overdue} testId="m-overdue" />
            </div>

            {err && <div className="brand-card mt-4" style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}>{err}</div>}

            {/* Assign week */}
            <div className="brand-card mt-6 fade-up" data-testid="at-assign-card">
                <p className="eyebrow flex items-center gap-2"><Calendar size={14}/> Assign this week</p>
                <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3 mt-3 items-end">
                    <div>
                        <label className="field-label">Module</label>
                        <select className="field-select" value={pickModule} onChange={(e) => setPickModule(e.target.value)} data-testid="at-pick-module">
                            <option value="">Choose a module…</option>
                            {modules.filter(m => m.is_active).map((m) => (
                                <option key={m.id} value={m.id}>{m.title} · {m.topic}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="field-label">Cohort</label>
                        <select className="field-select" value={pickCohort} onChange={(e) => setPickCohort(e.target.value)} data-testid="at-pick-cohort">
                            {COHORTS.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
                        </select>
                    </div>
                    <button type="button" className="btn btn-primary" onClick={assignWeek} disabled={busy || !pickModule} data-testid="at-assign-go">
                        <Save size={14}/> Assign
                    </button>
                </div>
                {savedMsg && <p className="text-sm mt-3" style={{ color: "var(--brand-success)" }} data-testid="at-assign-msg">{savedMsg}</p>}
            </div>

            {/* Modules library */}
            <div className="brand-card mt-6 fade-up" data-testid="at-modules-card">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <p className="eyebrow">Module library</p>
                    <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)} data-testid="at-new-module">
                        <Plus size={14}/> New module
                    </button>
                </div>
                <div className="overflow-x-auto mt-3">
                    <table className="brand-table" data-testid="at-modules-table">
                        <thead>
                            <tr>
                                <th>Title</th><th>Topic</th><th>Duration</th><th>Bunny</th><th>Active</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {modules.length === 0 && <tr><td colSpan={6} className="text-muted text-sm" style={{padding:"1rem"}}>No modules yet — create one to assign training.</td></tr>}
                            {modules.map((m) => (
                                <tr key={m.id} style={{ cursor: "default" }} data-testid={`at-module-${m.id}`}>
                                    <td><p className="font-display font-bold">{m.title}</p><p className="text-xs text-muted truncate" style={{maxWidth:280}}>{m.description}</p></td>
                                    <td className="capitalize text-sm">{m.topic}</td>
                                    <td className="text-sm">{Math.round(m.duration_seconds/60)} min</td>
                                    <td className="text-xs text-muted">{m.bunny_video_id ? "configured" : "—"}</td>
                                    <td>{m.is_active ? <span className="badge-pill badge-success">active</span> : <span className="badge-pill badge-muted">off</span>}</td>
                                    <td className="text-right">
                                        <button type="button" className="btn btn-ghost" onClick={() => removeModule(m.id)} data-testid={`at-del-${m.id}`}><Trash2 size={14}/></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Compliance */}
            <div className="brand-card mt-6 fade-up" data-testid="at-compliance-card">
                <p className="eyebrow">Compliance by user</p>
                <div className="overflow-x-auto mt-3">
                    <table className="brand-table">
                        <thead>
                            <tr><th>Name</th><th>Role</th><th>Total</th><th>Completed</th><th>Overdue</th><th>%</th></tr>
                        </thead>
                        <tbody>
                            {compliance.map((u) => (
                                <tr key={u.user_id} style={{cursor:"default"}}>
                                    <td><p className="font-display font-bold">{u.name}</p><p className="text-xs text-muted">{u.email}</p></td>
                                    <td className="capitalize text-sm">{u.role}</td>
                                    <td className="text-right">{u.assignments_total}</td>
                                    <td className="text-right">{u.assignments_completed}</td>
                                    <td className="text-right" style={{ color: u.assignments_overdue ? "var(--brand-red)" : "inherit" }}>{u.assignments_overdue}</td>
                                    <td className="text-right font-mono">{u.compliance_percent}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showCreate && <CreateModuleModal onClose={() => setShowCreate(false)} onCreated={async () => { setShowCreate(false); await load(); }} />}
        </div>
    );
}

function Metric({ label, value, testId }) {
    return (
        <div className="brand-card" data-testid={testId}>
            <p className="eyebrow">{label}</p>
            <p className="font-display font-bold mt-1" style={{ fontSize: "1.75rem", color: "var(--brand-red)" }}>{value}</p>
        </div>
    );
}

function CreateModuleModal({ onClose, onCreated }) {
    const [f, setF] = useState({ title: "", description: "", topic: "safety", duration_seconds: 300, bunny_library_id: "", bunny_video_id: "", script: "", is_active: true });
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);
    const [genBusy, setGenBusy] = useState(false);

    const gen = async () => {
        setGenBusy(true);
        setErr("");
        try {
            const { data } = await api.post("/admin/training/generate-script", { topic: f.topic, duration_minutes: Math.max(1, Math.round(f.duration_seconds / 60)) });
            setF((x) => ({ ...x, script: data.script }));
        } catch (e) { setErr(apiErrorMessage(e)); }
        finally { setGenBusy(false); }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr("");
        try {
            await api.post("/admin/training/modules", f);
            await onCreated();
        } catch (e) { setErr(apiErrorMessage(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" style={{ background: "rgba(26,26,26,0.5)" }} onClick={onClose} data-testid="at-create-modal">
            <form className="brand-card w-full max-w-[640px] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onSubmit={save}>
                <p className="eyebrow">New</p>
                <h2 className="h2 mt-2">Training module</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    <div className="sm:col-span-2">
                        <label className="field-label">Title</label>
                        <input className="field-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required data-testid="at-c-title"/>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="field-label">Description</label>
                        <textarea className="field-textarea" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="at-c-desc"/>
                    </div>
                    <div>
                        <label className="field-label">Topic</label>
                        <select className="field-select" value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} data-testid="at-c-topic">
                            {TOPICS.map((t) => <option key={t} value={t} className="capitalize">{t.replace("_", " ")}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="field-label">Duration (seconds)</label>
                        <input type="number" min={10} className="field-input" value={f.duration_seconds} onChange={(e) => setF({ ...f, duration_seconds: Number(e.target.value) })} required data-testid="at-c-duration"/>
                    </div>
                    <div>
                        <label className="field-label">Bunny library ID</label>
                        <input className="field-input" value={f.bunny_library_id} onChange={(e) => setF({ ...f, bunny_library_id: e.target.value })} data-testid="at-c-bunny-lib"/>
                    </div>
                    <div>
                        <label className="field-label">Bunny video ID</label>
                        <input className="field-input" value={f.bunny_video_id} onChange={(e) => setF({ ...f, bunny_video_id: e.target.value })} data-testid="at-c-bunny-vid"/>
                    </div>
                    <div className="sm:col-span-2">
                        <div className="flex items-baseline justify-between gap-3">
                            <label className="field-label">Script (optional)</label>
                            <button type="button" className="btn btn-ghost" onClick={gen} disabled={genBusy} data-testid="at-c-gen">
                                <Wand size={14}/> {genBusy ? "Generating…" : "Generate with AI"}
                            </button>
                        </div>
                        <textarea className="field-textarea font-mono" rows={6} value={f.script} onChange={(e) => setF({ ...f, script: e.target.value })} data-testid="at-c-script"/>
                    </div>
                </div>
                {err && <p className="field-error mt-2">{err}</p>}
                <div className="mt-5 flex items-center gap-3">
                    <button type="submit" className="btn btn-primary" disabled={saving} data-testid="at-c-save"><Save size={14}/> {saving ? "Saving…" : "Create module"}</button>
                    <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
                </div>
            </form>
        </div>
    );
}
